import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import type { PermissionModule, PermissionResource } from '../../api/settingsUsers'
import { assetUrl } from '../../ui/assetUrl'

/**
 * DREPTURILE, PE MODULE: coloana de module la stanga, resursele la dreapta.
 *
 * Portat din `views/version-3/settings/users/active/permissions.php` si din
 * `public/js/views/settings/users/active/permissions-1.js`, asa cum au fost
 * REDESENATE - nu din forma dinainte (o lista de module desfacute unul sub
 * altul).
 *
 * -----------------------------------------------------------------------------
 * DE CE ARATA ASA
 * -----------------------------------------------------------------------------
 *
 * Desfacute toate, modulele faceau o lista mai lunga decat tabelul de deasupra:
 * ca sa vezi al patrulea modul treceai cu ochii peste toate resursele primelor
 * trei, iar butonul de salvare fugea la cativa ecrani mai jos. Acum modulele
 * stau intr-o coloana ingusta la stanga - toate deodata, cu insigna fiecaruia -
 * si numai resursele modulului ales ocupa restul. Amandoua coloanele se opresc
 * la inaltimea ecranului si isi deruleaza continutul.
 *
 * TOATE RESURSELE RAMAN IN FORMULAR, si cele ascunse: ascunderea e doar CSS.
 * Aici starea e un tablou in React, nu DOM, deci lucrul asta e dat: se trimite
 * tot ce s-a bifat, oricare modul ar fi deschis.
 *
 * -----------------------------------------------------------------------------
 * CUM SE SALVEAZA
 * -----------------------------------------------------------------------------
 *
 * Capatul STERGE tot ce avea omul (sau modulul rolului) si insereaza cate un
 * rand pentru fiecare `access[<modul>_<resursa>]` primit. Deci:
 *
 *   o resursa cu o treapta bifata  -> se trimite -> se scrie randul
 *   o resursa cu nimic bifat       -> nu se trimite -> nu are dreptul
 *
 * "Fara drept" nu e o valoare care se trimite, ci LIPSA ei. Butonul de golire
 * de pe fiecare rand scoate alegerea; nu trimite nimic.
 *
 * -----------------------------------------------------------------------------
 * CELE PATRU TREPTE
 * -----------------------------------------------------------------------------
 *
 * Valorile raman 1-4 pentru toata lumea. Ce INSEAMNA fiecare treapta e treaba
 * resursei: aproape peste tot Read/Create/Edit/Delete, iar Adam Intop are alte
 * doua scari. Numele vin de la server, in `levels`.
 *
 * "Select all" pune PRIMA treapta peste tot, nu ultima: e cea mai mica, si un
 * buton comod care ar da din greseala tuturor dreptul de sters ar fi o unealta
 * periculoasa langa una comoda.
 */

/** Stilul, in linie: panoul vine intr-o fila deschisa dupa incarcarea paginii. */
const CSS = `
.od-perm { --od-line: #e3e8ee; --od-ink: #33383d; --od-mut: #8a949e; --od-sel: #1d6a86; }
.od-perm { max-width: min(1180px, calc(100vw - 120px)); }
.od-perm-top { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 0 0 12px; }
.od-perm-find { flex: 1 1 220px; min-width: 180px; height: 34px; padding: 0 12px; border: 1px solid var(--od-line); border-radius: 8px; font-size: 13px; }
.od-perm-top .od-perm-bulk { margin-left: auto; }
.od-perm-bulk { display: flex; gap: 6px; }
.od-perm-bulk button { height: 30px; padding: 0 12px; font-size: 12px; font-weight: 600; color: var(--od-ink); background: #fff; border: 1px solid var(--od-line); border-radius: 7px; cursor: pointer; }
.od-perm-bulk button:hover { background: #f4f7f9; }
.od-perm-wrap { display: flex; gap: 0; align-items: stretch; border: 1px solid var(--od-line); border-radius: 10px; background: #fff; overflow: hidden; }
.od-perm-rail, .od-perm-panes { max-height: 58vh; min-height: 260px; overflow-y: auto; }
.od-perm-rail { flex: 0 0 232px; border-right: 1px solid var(--od-line); background: #fafbfc; padding: 6px 0; }
.od-perm-panes { flex: 1 1 auto; padding: 0; }
.od-perm-tab { display: flex; align-items: center; gap: 9px; width: 100%; padding: 8px 12px; border: 0; background: transparent; text-align: left; cursor: pointer; border-left: 3px solid transparent; }
.od-perm-tab:hover { background: #f1f5f8; }
.od-perm-tab.is-on { background: #fff; border-left-color: var(--od-sel); }
.od-perm-tab img { width: 18px; height: auto; flex: 0 0 18px; }
.od-perm-name { font-size: 13px; font-weight: 600; color: var(--od-ink); flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.od-perm-tab.is-on .od-perm-name { color: var(--od-sel); }
.od-perm-count { flex: 0 0 auto; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; background: #eef2f6; color: var(--od-mut); }
.od-perm-count.is-on { background: var(--od-sel); color: #fff; }
.od-perm-pane-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 10px 14px; border-bottom: 1px solid var(--od-line); position: sticky; top: 0; background: #fff; z-index: 1; }
.od-perm-pane-name { font-weight: 700; font-size: 13px; color: var(--od-ink); }
.od-perm-pane-head .od-perm-bulk { margin-left: auto; }
.od-perm-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 9px 14px; border-bottom: 1px solid #f4f7f9; }
.od-perm-row:last-child { border-bottom: 0; }
.od-perm-res { font-size: 13px; color: var(--od-ink); flex: 1 1 200px; }
.od-perm-steps { display: flex; border: 1px solid var(--od-line); border-radius: 8px; overflow: hidden; }
.od-perm-steps label { margin: 0; padding: 5px 11px; font-size: 12px; font-weight: 600; color: var(--od-mut); background: #fff; cursor: pointer; border-right: 1px solid var(--od-line); white-space: nowrap; }
.od-perm-steps label:last-child { border-right: 0; }
.od-perm-steps input { position: absolute; opacity: 0; pointer-events: none; }
.od-perm-steps label.is-on { background: var(--od-sel); color: #fff; }
.od-perm-steps label:hover { background: #f4f7f9; }
.od-perm-steps label.is-on:hover { background: #17596f; }
.od-perm-clear { border: 0; background: transparent; color: var(--od-mut); font-size: 15px; line-height: 1; padding: 4px 6px; cursor: pointer; border-radius: 6px; }
.od-perm-clear:hover { background: #fdecea; color: #d83430; }
.od-perm-row.is-empty .od-perm-clear { visibility: hidden; }
.od-perm-none { padding: 14px; color: var(--od-mut); font-size: 13px; }
.od-perm-save { margin-top: 14px; }
@media (max-width: 860px) {
  .od-perm-wrap { display: block; }
  .od-perm-rail { flex: none; max-height: 190px; min-height: 0; border-right: 0; border-bottom: 1px solid var(--od-line); }
  .od-perm-panes { max-height: none; min-height: 0; }
}
`

export type PermissionMap = Record<string, number>

/** Cheia unui rand, ca in portal: `<modul>_<resursa>`. */
export function permKey(resource: PermissionResource): string {
  return `${resource.moduleId}_${resource.id}`
}

/**
 * Ce e bifat la intrare, din ce a intors serverul - plafonat la capatul
 * scarii resursei. `adam_intop.access` are numai trei trepte (pana la
 * Incident); un 4 ramas in baza se arata si se trimite inapoi ca 3, nu ca o
 * treapta fara eticheta pe care serverul ar refuza-o la salvare.
 */
export function initialPermissions(resources: PermissionResource[]): PermissionMap {
  const map: PermissionMap = {}
  for (const resource of resources) {
    const top = resource.levels.length > 0 ? resource.levels.length : 4
    const level = Math.min(resource.permission, top)
    if (level > 0) map[permKey(resource)] = level
  }
  return map
}

export function PermissionsGrid({
  modules,
  resources,
  chosen,
  onChange,
  onSubmit,
  saving,
  saveLabel = 'Save changes',
  extraActions,
}: {
  modules: PermissionModule[]
  resources: PermissionResource[]
  chosen: PermissionMap
  onChange(next: PermissionMap): void
  onSubmit(): void
  saving: boolean
  saveLabel?: string
  /** Ce mai sta langa Save (la roluri: "Remove module"). */
  extraActions?(moduleId: number): ReactNode
}) {
  const [find, setFind] = useState('')
  const [open, setOpen] = useState(0)

  const byModule = useMemo(() => {
    const map = new Map<number, PermissionResource[]>()
    for (const resource of resources) {
      const list = map.get(resource.moduleId)
      if (list) list.push(resource)
      else map.set(resource.moduleId, [resource])
    }
    return map
  }, [resources])

  /** Cate resurse ale modulului au o treapta - insigna din coloana. */
  const counts = useMemo(() => {
    const map = new Map<number, { on: number; of: number }>()
    for (const module of modules) {
      const list = byModule.get(module.id) ?? []
      const on = list.filter((r) => (chosen[permKey(r)] ?? 0) > 0).length
      map.set(module.id, { on, of: list.length })
    }
    return map
  }, [modules, byModule, chosen])

  /*
   * Modulul deschis la intrare: primul care are deja ceva bifat, altfel primul
   * din lista. Cine deschide fisa unui om vrea sa vada intai ce ARE.
   */
  useEffect(() => {
    if (open !== 0 || modules.length === 0) return
    const withSome = modules.find((m) => (counts.get(m.id)?.on ?? 0) > 0)
    setOpen((withSome ?? modules[0]).id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules])

  const needle = find.trim().toLowerCase()

  const matches = (module: PermissionModule, resource: PermissionResource): boolean =>
    needle === '' || `${module.name} ${resource.name}`.toLowerCase().includes(needle)

  /** Modulele care mai au ceva de aratat dupa cautare. */
  const visibleModules = modules.filter(
    (module) => (byModule.get(module.id) ?? []).some((resource) => matches(module, resource)),
  )

  /* Cand cautarea scoate modulul deschis, se trece pe primul care are ceva -
     altfel cauti si te uiti la un panou gol. */
  useEffect(() => {
    if (visibleModules.length === 0) return
    if (visibleModules.some((m) => m.id === open)) return
    setOpen(visibleModules[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needle, visibleModules.length])

  const openModule = modules.find((m) => m.id === open) ?? modules[0]

  /** Pune sau scoate treapta pe randurile date. */
  const setRows = (rows: PermissionResource[], what: 'first' | 'none') => {
    const next = { ...chosen }
    for (const resource of rows) {
      if (what === 'none') delete next[permKey(resource)]
      else next[permKey(resource)] = 1
    }
    onChange(next)
  }

  const pick = (resource: PermissionResource, level: number) => {
    onChange({ ...chosen, [permKey(resource)]: level })
  }

  const clear = (resource: PermissionResource) => {
    const next = { ...chosen }
    delete next[permKey(resource)]
    onChange(next)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    onSubmit()
  }

  /* Cele doua butoane de sus lucreaza pe TOT formularul - si pe modulele care
     nu se vad acum. Ce e ascuns de CAUTARE nu se atinge: acolo ai filtrat tu.
     Un modul nedeschis nu e o filtrare, e celalalt panou al aceleiasi liste. */
  const allShown = modules.flatMap((module) =>
    (byModule.get(module.id) ?? []).filter((resource) => matches(module, resource)),
  )

  const openRows = openModule ? (byModule.get(openModule.id) ?? []).filter((r) => matches(openModule, r)) : []

  return (
    <form className="od-perm" method="post" onSubmit={submit}>
      <style>{CSS}</style>

      <div className="od-perm-top">
        <input
          type="search"
          className="od-perm-find"
          placeholder="Search a module or a right&hellip;"
          autoComplete="off"
          aria-label="Search a module or a right"
          value={find}
          onChange={(e) => setFind(e.target.value)}
        />

        <div className="od-perm-bulk">
          <button type="button" onClick={() => setRows(allShown, 'first')}>
            Select all
          </button>
          <button type="button" onClick={() => setRows(allShown, 'none')}>
            Deselect all
          </button>
        </div>
      </div>

      <div className="od-perm-wrap">
        <div className="od-perm-rail" role="tablist">
          {visibleModules.map((module) => {
            const count = counts.get(module.id) ?? { on: 0, of: 0 }
            const on = module.id === openModule?.id
            return (
              <button
                key={module.id}
                type="button"
                role="tab"
                aria-selected={on}
                className={on ? 'od-perm-tab is-on' : 'od-perm-tab'}
                onClick={() => setOpen(module.id)}
              >
                <img src={assetUrl('images/header/' + module.icon)} alt="" />
                <span className="od-perm-name">{module.name}</span>
                <span className={count.on > 0 ? 'od-perm-count is-on' : 'od-perm-count'}>
                  {count.on} / {count.of}
                </span>
              </button>
            )
          })}
        </div>

        <div className="od-perm-panes">
          {openModule ? (
            <div className="od-perm-pane is-on" data-module={openModule.id}>
              <div className="od-perm-pane-head">
                <span className="od-perm-pane-name">{openModule.name}</span>
                <div className="od-perm-bulk">
                  <button type="button" onClick={() => setRows(openRows, 'first')}>
                    Select all
                  </button>
                  <button type="button" onClick={() => setRows(openRows, 'none')}>
                    Deselect all
                  </button>
                </div>
              </div>

              {openRows.map((resource) => {
                const level = chosen[permKey(resource)] ?? 0
                return (
                  <div key={resource.id} className={level > 0 ? 'od-perm-row' : 'od-perm-row is-empty'}>
                    <span className="od-perm-res">{resource.name}</span>

                    <div className="od-perm-steps">
                      {resource.levels.map((name, index) => {
                        const value = index + 1
                        const id = `access_${value}_${resource.moduleId}_${resource.id}`
                        return (
                          <label key={name} htmlFor={id} className={level === value ? 'is-on' : ''}>
                            <input
                              type="radio"
                              id={id}
                              name={`access[${permKey(resource)}]`}
                              value={value}
                              checked={level === value}
                              onChange={() => pick(resource, value)}
                            />
                            <span>{name}</span>
                          </label>
                        )
                      })}
                    </div>

                    <button
                      type="button"
                      className="od-perm-clear"
                      title="No right on this"
                      onClick={() => clear(resource)}
                    >
                      &times;
                    </button>
                  </div>
                )
              })}

              {openRows.length === 0 ? <p className="od-perm-none">This module has no rights to give.</p> : null}
            </div>
          ) : null}

          {needle !== '' && allShown.length === 0 ? (
            <p className="od-perm-none">Nothing matches what you typed.</p>
          ) : null}
        </div>
      </div>

      <div className="od-perm-save">
        <button className="btn btn-success" type="submit" disabled={saving}>
          {saving ? 'Saving...' : saveLabel}
        </button>
        {openModule && extraActions ? extraActions(openModule.id) : null}
      </div>
    </form>
  )
}
